import { PARTNER_SIGN_IN_CLIENT } from '@instanexus/contracts';
import { useTranslation } from '@instanexus/i18n';
import { Button, Card, CardFooter, CardHeader } from '@instanexus/ui';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { clientAppOrigin, parseSignInRequest } from '@/lib/sign-in-request';

/**
 * The central sign-out page (ADR 0038 decision 6). Ends the central session, then returns the
 * browser to the client app's root. With no origin configured for the client it stays and offers
 * the sign-in link instead.
 */
export function SignOutPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const [searchParams] = useSearchParams();
  const { client } = useMemo(() => parseSignInRequest(searchParams), [searchParams]);
  const [stayed, setStayed] = useState(false);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async (): Promise<void> => {
      try {
        if (client === PARTNER_SIGN_IN_CLIENT) await api.partnerAuth.logout();
        else await api.auth.logout();
      } catch {
        // Idempotent and best-effort, as in every app: leave regardless; the next sign-out retries.
      }
      const origin = clientAppOrigin(client);
      if (origin === null) {
        setStayed(true);
        return;
      }
      window.location.replace(new URL('/', origin).toString());
    })();
  }, [api, client]);

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <p role="status" className="text-muted-foreground">
            {t('central.signingOut')}
          </p>
        </CardHeader>
        {stayed ? (
          <CardFooter>
            <Button asChild>
              <a href={`/login?client=${encodeURIComponent(client)}`}>{t('central.signInAgain')}</a>
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </AuthCardLayout>
  );
}
